import { useToast } from '@/context/ToastContext';
import { useCartQuantity, useDeleteCartItem } from '@/features/cart/hooks';
import { useProducts } from '@/features/product/hooks';
import type { CartItem } from '@/schema/cart.schema';
import { priceFormat } from '@/utils/format';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Minus, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Icon } from '../shared/Icon';

export const CartCard = (cart: CartItem) => {
    const { product, price, quantity } = cart ?? {};

    const { data } = useQuery(useProducts());
    const productData = data?.product.find(prod => prod._id === product?._id);
    const productStock = productData?.stocks;
    const latestPrice = productData?.discountPrice ?? productData?.price;

    const [quantityCount, setQuantityCount] = useState(quantity);

    const updateQuantityMutation = useMutation(useCartQuantity());
    const deleteCartItem = useMutation(useDeleteCartItem());

    const { showToast } = useToast();

    const updateQuantity = (newQuantity: number) => {
        if (newQuantity < 1) return;
        if (typeof productStock === 'number' && newQuantity > productStock) {
            showToast('warning', `Only ${productStock} in stock`);
            return;
        }
        setQuantityCount(newQuantity);
        updateQuantityMutation.mutate({ productId: product._id as string, quantity: newQuantity });
    };

    const handleIncrease = () => updateQuantity(quantityCount + 1);
    const handleDecrease = () => updateQuantity(quantityCount - 1);

    const stockStatus =
        typeof productStock !== 'number'
            ? { label: 'Checking availability', className: 'text-secondary' }
            : productStock <= 0
            ? { label: 'Out of stock', className: 'text-red-500' }
            : quantityCount > productStock
            ? { label: `Only ${productStock} left`, className: 'text-yellow-600' }
            : { label: 'In stock', className: 'text-green-600' };

    const priceStatus =
        typeof latestPrice !== 'number'
            ? { label: 'Checking price', className: 'text-secondary' }
            : latestPrice !== price
            ? {
                  label: `Price updated: ${priceFormat(latestPrice)}`,
                  className: 'text-yellow-600',
              }
            : { label: 'Price verified', className: 'text-green-600' };

    return (
        <div className="relative rounded-xl gap-5 p-5 bg-light-fg dark:bg-dark-surface flex md:flex-row flex-col items-center">
            <div className="bg-light-bg dark:bg-dark-fg aspect-square rounded-xl h-60 w-full md:size-40 p-5">
                <img
                    src={product?.images[0].url}
                    alt=""
                    className="h-full object-cover w-full"
                />
            </div>
            <div className="w-full">
                <h2 className="text-2xl text-coral-black dark:text-white font-bold">
                    {product?.name}
                </h2>
                <h6 className="mt-2 font-bold text-primary text-lg">{priceFormat(price)}</h6>
                <div className="mt-2 flex flex-col gap-1 text-sm">
                    <span className={stockStatus.className}>{stockStatus.label}</span>
                    <span className={priceStatus.className}>{priceStatus.label}</span>
                </div>
                {/* <p className="mt-2 text-sm text-secondary">{spec}</p> */}
                <div className="flex justify-between w-full mt-4">
                    <div className="bg-light-fg flex px-4 gap-5 rounded-xl py-2 w-30">
                        <button onClick={handleDecrease}>
                            <Icon icon={Minus} size={15} />
                        </button>
                        <input
                            value={quantityCount}
                            onChange={e => setQuantityCount(e.target.valueAsNumber)}
                            type="number"
                            className="w-full text-center"
                        />
                        <button onClick={handleIncrease}>
                            <Icon icon={Plus} size={15} />
                        </button>
                    </div>
                </div>
            </div>
            <button
                onClick={() =>
                    deleteCartItem.mutate(product._id as string, {
                        onSuccess: () =>
                            showToast(
                                'success',
                                `${product.name} removed from the cart successfully`
                            ),
                    })
                }
                className="absolute top-0 right-0 m-4 text-secondary"
            >
                <Icon icon={Trash2} />
            </button>
        </div>
    );
};
